/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging";
import { ThumbnailStatus } from "../enums";
import type { TThumbnailEntry } from "../types";
import {
  buildThumbnailUrl,
  snapToStep,
  THUMBNAIL_INTERVAL
} from "./ThumbnailGrid";
import { DEFAULT_MAX_ACCEPTABLE_GAP } from "./ThumbnailManager10FootConfig";
import { clearTimer } from "./ThumbnailSession";
import type { ThumbnailSession } from "./ThumbnailSession";

/**
 * The original eager, whole-asset thumbnail strategy used on desktop browsers.
 *
 * Shape: FIRE-AND-FORGET. It is told to load once, builds an entry for every
 * thumbnail in the asset, fetches them in bisection order until the queue
 * drains, and then only answers lookups until teardown. It has no sliding
 * window, no eviction and no notion of a seek session — which is exactly why
 * it is unsuitable for memory-constrained TVs and why the 10-foot strategy
 * exists alongside it.
 *
 * Port of mobi-client-connect-web ThumbnailManager.js.
 */

/** Maximum number of parallel thumbnail fetch requests. */
const MAX_PARALLEL_REQUESTS: number = 4;

/** Maximum thumbnails to include in the fetch queue (~2 hours of content). */
const MAX_THUMBNAILS_TO_FETCH: number = 600;

/** Divisor used for binary bisection when building the prefetch priority queue. */
const FETCH_QUEUE_BISECTION_DIVISOR: number = 2;

/** Live thumbnails are only fetched when >= this many seconds behind the live edge. */
const LIVE_THRESHOLD_SECONDS: number = 60;

export class BrowserThumbnailStrategy {
  private static TAG: string = "ThumbnailManager";

  /** All thumbnail entries in chronological order. */
  private mThumbnails: TThumbnailEntry[] = [];

  /** Quick lookup by time → entry. */
  private mThumbnailsList: Map<number, TThumbnailEntry> = new Map();

  /** Ordered list of entries still waiting to be fetched. */
  private mPrefetchQueue: TThumbnailEntry[] = [];

  /** Currently in-flight fetches keyed by time. */
  private mFetchesInFlight: Map<number, TThumbnailEntry> = new Map();

  /** Content start epoch (seconds) or 0 for pure VOD. */
  private mStartTime: number = 0;

  /** Content end epoch (seconds) or duration for VOD. */
  private mEndTime: number = 0;

  /** URL template with {FILENAME} placeholder. */
  private mUrlTemplate: string = "";

  /** Whether the content is a live stream. */
  private mIsLive: boolean = false;

  /** Interval between thumbnails (seconds). */
  private mThumbnailsInterval: number = THUMBNAIL_INTERVAL;

  /** Live-edge timer handle. */
  private mLiveFetchTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Next live thumbnail time to schedule. */
  private mNextLiveThumbnailToFetch: number | null = null;

  /** True while fetching is suspended — see setFetchSuspended(). */
  private mIsSuspended: boolean = false;

  private readonly mSession: ThumbnailSession;

  /**
   * @param session - Shared session state (generation token, last-known-good
   *                  frame, diagnostics counters).
   */
  public constructor(session: ThumbnailSession) {
    this.mSession = session;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Store the content parameters. Deliberately does no work — the eager build
   * and prefetch are deferred to start(), which the manager calls only once
   * LOADED + FIRST_FRAME have fired and the debounce has elapsed, so thumbnail
   * traffic never competes with stream start-up.
   */
  public load(
    startTime: number,
    endTime: number,
    urlTemplate: string,
    isLive: boolean
  ): void {
    this.mStartTime = startTime;
    this.mEndTime = endTime;
    this.mUrlTemplate = urlTemplate;
    this.mIsLive = isLive;
  }

  /** Build every entry, order the queue by bisection, and begin prefetching. */
  public start(): void {
    this.buildThumbnailEntries();
    this.buildPrefetchPriorityQueue();
    this.prefetchThumbnails();
  }

  /**
   * Suspend or resume fetching without discarding the cache or queue — used
   * while an error OSD/Dialog covers the player (and while no-network
   * disables trick-play), the same signal the 10-foot strategy suspends on.
   *
   * Unlike reset(), already-fetched thumbnails and the remaining prefetch
   * queue survive: suspending only stops the queue and the live-edge timer
   * from starting further fetches, and resuming picks the queue back up
   * where it left off. Requests already in flight at the moment of
   * suspension are not aborted — allowing the handful (at most
   * MAX_PARALLEL_REQUESTS) already sent to complete is simpler and safer
   * than aborting and re-queuing them, and their completion callbacks
   * naturally stop propagating further fetches while suspended.
   *
   * @param suspended - True to suspend, false to resume.
   */
  public setFetchSuspended(suspended: boolean): void {
    if (this.mIsSuspended === suspended) {
      return;
    }
    this.mIsSuspended = suspended;
    if (suspended) {
      this.mLiveFetchTimeout = clearTimer(this.mLiveFetchTimeout);
      return;
    }
    this.prefetchThumbnails();
  }

  /**
   * Return the blob URL of the thumbnail closest to `seekPositionSeconds`.
   *
   * On a miss this returns the last genuinely-loaded frame, never a raw CDN
   * URL — handing back an unfetched URL would flash blank in the <img>. Null
   * only before anything has ever loaded for the current content.
   *
   * @param seekPositionSeconds - Position relative to content start.
   */
  public getThumbnail(seekPositionSeconds: number): string | null {
    // Convert the relative seek position (0..duration) to the absolute time
    // used as key in the thumbnail map (epoch-based for recordings/live).
    const seekTime = seekPositionSeconds + this.mStartTime;

    // Snap on the SAME mStartTime-anchored grid buildThumbnailEntries() keys
    // entries on.  Snapping to global interval multiples only coincides with
    // the entry grid when mStartTime % interval === 0 — so any off-minute
    // airing start made every lookup and every neighbour probe miss (silently,
    // since the last-known-good fallback then just holds the previous frame).
    // Lower bound needs no clamp — snapToStep() never returns below mStartTime.
    let closestTime = this.snapToThumbnailGrid(seekTime);

    // Ceiling is the last BUILT entry, not mEndTime: mEndTime is generally
    // off-grid and has no entry in mThumbnailsList.
    const maxThumbnailTime = this.getMaxThumbnailTime();
    if (closestTime > maxThumbnailTime) {
      closestTime = maxThumbnailTime;
    }

    // Try exact match first.
    const exact = this.mThumbnailsList.get(closestTime);
    if (exact && exact.status === ThumbnailStatus.LOADED && exact.imgBlob) {
      this.mSession.setLastDelivered(exact.imgBlob);
      return exact.imgBlob;
    }

    // Fallback: search both backward and forward within the acceptable gap.
    for (let i = 1; i <= DEFAULT_MAX_ACCEPTABLE_GAP; i++) {
      const backward = closestTime - this.mThumbnailsInterval * i;
      const forward = closestTime + this.mThumbnailsInterval * i;

      const backCandidate = this.mThumbnailsList.get(backward);
      if (
        backCandidate &&
        backCandidate.status === ThumbnailStatus.LOADED &&
        backCandidate.imgBlob
      ) {
        this.mSession.setLastDelivered(backCandidate.imgBlob);
        return backCandidate.imgBlob;
      }

      const fwdCandidate = this.mThumbnailsList.get(forward);
      if (
        fwdCandidate &&
        fwdCandidate.status === ThumbnailStatus.LOADED &&
        fwdCandidate.imgBlob
      ) {
        this.mSession.setLastDelivered(fwdCandidate.imgBlob);
        return fwdCandidate.imgBlob;
      }
    }

    // Nothing loaded nearby for the current position — make sure it's on its
    // way (jump the queue if it isn't already loading), but never hand back
    // its unfetched raw URL: that would flash blank/broken in the <img> until
    // the fetch completes. Keep showing the last genuinely-loaded frame
    // instead; it upgrades to the real one the moment the fetch lands.
    // Only null if nothing has ever loaded for this content.
    if (exact) {
      this.prioritize(exact);
    }

    return this.mSession.getLastDelivered();
  }

  /**
   * Abort in-flight fetches, revoke every object URL, and clear all state.
   *
   * The caller must have invalidated the session generation FIRST: aborting
   * cannot stop a response whose body is already resolving, so the token is
   * what prevents a late callback creating a blob URL this reset can no longer
   * revoke.
   */
  public reset(): void {
    this.mFetchesInFlight.forEach((entry) => {
      if (entry.abortController) {
        entry.abortController.abort();
      }
    });

    // Revoke object URLs to release memory held by fetched thumbnail blobs.
    for (const entry of this.mThumbnails) {
      if (entry.imgBlob) {
        URL.revokeObjectURL(entry.imgBlob);
        this.mSession.recordBlobRevoked();
      }
    }

    this.mThumbnails = [];
    this.mThumbnailsList = new Map();
    this.mPrefetchQueue = [];
    this.mFetchesInFlight = new Map();
    this.mStartTime = 0;
    this.mEndTime = 0;
    this.mUrlTemplate = "";
    this.mIsLive = false;
    this.mNextLiveThumbnailToFetch = null;
    this.mLiveFetchTimeout = clearTimer(this.mLiveFetchTimeout);
    // Mirrors ThumbnailManager's 10-foot reset — the next poll tick
    // re-suspends within one cycle if the condition still holds.
    this.mIsSuspended = false;
  }

  // ── Private — Entry building ───────────────────────────────────────────────

  /**
   * Populate `mThumbnails` with one entry per thumbnail interval.
   * Port of mobi `setThumbnailsArray()`.
   */
  private buildThumbnailEntries(): void {
    this.mThumbnails = [];
    this.mThumbnailsList = new Map();

    const now = Math.floor(Date.now() / 1000);
    this.mThumbnailsInterval = THUMBNAIL_INTERVAL;

    let nextSegment = this.mStartTime;

    while (nextSegment < this.mEndTime) {
      // Evaluated per offset rather than by incrementing a running filename:
      // consecutive thumbnails are exactly THUMBNAIL_FILENAME_STEP segments
      // apart, so the two are arithmetically identical — and sharing the helper
      // keeps this on the same convention as the 10-foot URL builder.
      // Null only for an empty template, which the manager already rejects;
      // "" preserves the previous String.replace() behaviour exactly.
      const built = buildThumbnailUrl(this.mUrlTemplate, nextSegment);
      const url = built === null ? "" : built;

      const entry: TThumbnailEntry = {
        time: nextSegment,
        url,
        status: ThumbnailStatus.PENDING,
        imgBlob: null,
        // AbortController is created at fetch time (not here) to avoid
        // the race condition where reset() fires the signal before
        // fetchThumbnail() even starts.
        abortController: null
      };

      this.mThumbnails.push(entry);

      nextSegment = nextSegment + this.mThumbnailsInterval;

      // Track where live thumbnails start being too close to the edge.
      if (
        this.mIsLive &&
        this.mNextLiveThumbnailToFetch === null &&
        nextSegment > now - LIVE_THRESHOLD_SECONDS
      ) {
        this.mNextLiveThumbnailToFetch = nextSegment;
      }
    }
  }

  /**
   * Build the fetch queue using a binary insertion algorithm.
   * Order: edges → midpoint → quarter-points → eighth-points → …
   *
   * For 11 thumbnails: 0, 5, 10, 2, 4, 6, 8, 1, 3, 7, 9
   *
   * The rationale is that a user is equally likely to scrub anywhere, so
   * coarse-then-fine coverage of the whole asset beats front-to-back.
   *
   * Port of mobi `setThumbnailsToBeFetched()`.
   */
  private buildFetchQueue(): TThumbnailEntry[] {
    const queue: TThumbnailEntry[] = [];
    const added = new Set<number>();

    const maxLength =
      this.mThumbnails.length > MAX_THUMBNAILS_TO_FETCH
        ? MAX_THUMBNAILS_TO_FETCH
        : this.mThumbnails.length;

    let interval = Math.floor(
      this.mThumbnails.length / FETCH_QUEUE_BISECTION_DIVISOR
    );

    while (queue.length < maxLength && interval >= 1) {
      for (
        let i = 0;
        i < this.mThumbnails.length && queue.length < maxLength;
        i = i + interval
      ) {
        if (!added.has(i)) {
          added.add(i);
          queue.push(this.mThumbnails[i]);
        }
      }
      interval = Math.floor(interval / FETCH_QUEUE_BISECTION_DIVISOR);
    }

    // Ensure remaining entries (if interval never reached them) are added.
    for (
      let i = 0;
      i < this.mThumbnails.length && queue.length < maxLength;
      i++
    ) {
      if (!added.has(i)) {
        added.add(i);
        queue.push(this.mThumbnails[i]);
      }
    }

    // Populate the lookup map with ALL entries, not just the capped fetch
    // queue, so getThumbnail() still resolves positions that were never queued.
    for (const entry of this.mThumbnails) {
      this.mThumbnailsList.set(entry.time, entry);
    }

    return queue;
  }

  /**
   * Build the prefetch priority queue, filtering out live thumbnails
   * that are too close to the live edge (they would 404).
   *
   * Port of mobi `setPrefetchPriorityQueue()`.
   */
  private buildPrefetchPriorityQueue(): void {
    const fetchQueue = this.buildFetchQueue();

    if (this.mIsLive) {
      const now = Math.floor(Date.now() / 1000);
      const liveThreshold = now - LIVE_THRESHOLD_SECONDS;
      this.mPrefetchQueue = fetchQueue.filter(
        (entry) => entry.time <= liveThreshold
      );
    } else {
      this.mPrefetchQueue = fetchQueue;
    }
  }

  // ── Private — Fetching ─────────────────────────────────────────────────────

  /**
   * Kick off parallel prefetching (up to MAX_PARALLEL_REQUESTS at a time).
   *
   * Port of mobi `prefetchThumbnails()`.
   */
  private prefetchThumbnails(): void {
    if (this.mIsSuspended) {
      return;
    }
    const batch = this.mPrefetchQueue.splice(0, MAX_PARALLEL_REQUESTS);
    for (const entry of batch) {
      this.fetchThumbnail(entry);
    }

    // Schedule live thumbnail fetching if applicable.
    if (this.mIsLive) {
      this.scheduleLiveThumbnailFetch();
    }
  }

  /**
   * Dequeue and fetch the next thumbnail in the priority queue.
   *
   * Guarded on mIsSuspended rather than leaving it to fetchThumbnail(): this
   * is reached from onFetchSuccess()/onFetchError() while a request already
   * in flight at suspend time completes, and shifting the entry off the
   * queue before finding out fetchThumbnail() will no-op would silently drop
   * it — it would never be retried once resumed.
   */
  private prefetchNextImage(): void {
    if (this.mIsSuspended) {
      return;
    }
    const nextEntry = this.mPrefetchQueue.shift();
    if (nextEntry) {
      this.fetchThumbnail(nextEntry);
    }
  }

  /**
   * Move an on-demand-requested thumbnail to the front of the prefetch queue
   * (or fetch it immediately if a concurrency slot is free), so a true cache
   * miss for the user's current position doesn't have to wait behind the rest
   * of the bisection-ordered queue. Does not alter the bisection ordering
   * itself — only this one entry jumps the line.
   *
   * FAILED is treated as terminal for this lookup-driven path.  Callers reach
   * here once per render while the pointer hovers, and buildThumbnailEntries()
   * covers the whole airing window including not-yet-aired frames — so without
   * this guard a hover past the live edge re-requests a 404ing frame on every
   * render, and each failure's onFetchError() pulls another entry out of the
   * bisection queue.  Timer- and batch-driven enqueues call fetchThumbnail()
   * directly and are unaffected, so they still retry normally.
   */
  private prioritize(entry: TThumbnailEntry): void {
    if (this.mIsSuspended) {
      return;
    }
    if (
      entry.status === ThumbnailStatus.LOADING ||
      entry.status === ThumbnailStatus.LOADED ||
      entry.status === ThumbnailStatus.FAILED
    ) {
      return;
    }
    const queueIndex = this.mPrefetchQueue.indexOf(entry);
    if (queueIndex !== -1) {
      this.mPrefetchQueue.splice(queueIndex, 1);
    }
    if (this.mFetchesInFlight.size < MAX_PARALLEL_REQUESTS) {
      this.fetchThumbnail(entry);
    } else {
      this.mPrefetchQueue.unshift(entry);
    }
  }

  /**
   * Fetch a single thumbnail image and store it as a blob object URL.
   *
   * Re-fetching an entry that is already LOADED or LOADING is refused, and that
   * guard is load-bearing rather than defensive. getCurrentUrlForEntry() hands
   * back entry.imgBlob once one exists, so a second fetch would download the
   * `blob:` URL itself and mint a SECOND object URL over the top — leaving the
   * first unreachable and unrevokable for the document's lifetime, which is
   * precisely the native-memory leak the session generation guard exists to
   * prevent. The live-edge timer was the one caller that reached here
   * unguarded, so this is enforced centrally instead of at each call site.
   *
   * FAILED is deliberately NOT refused: the commonest failure is a frame just
   * past the live edge whose segment is not on the CDN yet, and the timer- and
   * batch-driven paths are expected to retry it.
   *
   * Port of mobi `prefetchImage()`, which used base64; see the note at the
   * blob callback for why this does not.
   */
  private fetchThumbnail(entry: TThumbnailEntry): void {
    if (!entry || this.mIsSuspended) {
      return;
    }
    if (
      entry.status === ThumbnailStatus.LOADED ||
      entry.status === ThumbnailStatus.LOADING
    ) {
      return;
    }

    entry.status = ThumbnailStatus.LOADING;
    // Create the AbortController here (not at entry-build time) so that a
    // reset() before prefetching starts does not pre-fire the abort signal and
    // immediately reject in-flight fetches.
    entry.abortController = new AbortController();
    this.mFetchesInFlight.set(entry.time, entry);

    const url = this.getCurrentUrlForEntry(entry);
    if (!url) {
      this.onFetchError(entry, "no URL for entry");
      return;
    }

    this.mSession.fetchGuarded(
      url,
      entry.abortController.signal,
      (blobUrl: string): void => {
        // An object URL rather than FileReader.readAsDataURL: base64 would
        // duplicate the blob in memory ~33% larger, which matters at 600
        // entries on SmartTV.
        entry.imgBlob = blobUrl;
        this.onFetchSuccess(entry);
      },
      (reason: unknown): void => {
        this.onFetchError(entry, reason);
      },
      (): void => {
        // Aborts are expected during reset() and need no bookkeeping on this
        // path — there is no concurrency counter here, and reset() clears
        // mFetchesInFlight wholesale.
      }
    );
  }

  /**
   * Handle a successful thumbnail prefetch: mark loaded, dequeue the next entry.
   * @param entry - The TThumbnailEntry whose fetch just completed successfully.
   */
  private onFetchSuccess(entry: TThumbnailEntry): void {
    entry.status = ThumbnailStatus.LOADED;
    this.mFetchesInFlight.delete(entry.time);
    this.prefetchNextImage();
    // Same contract as the 10-foot path: a lookup that missed was handed the
    // last-known-good frame, and this is how it learns the real one arrived.
    this.mSession.notifyThumbnailAvailable();
  }

  /**
   * Handle a failed thumbnail prefetch: mark failed, log, continue the queue.
   *
   * Logged at WARN, not ERROR, as a deliberate deviation from CHECKLIST H-04 /
   * CodingGuidelines §13.3.1: a thumbnail that 404s just past the live edge is
   * an expected, benign condition, and escalating every one would flood
   * error-level logs on live content.  §14.2 designates WARN as "degraded but
   * functional", which is exactly this case — playback is unaffected and the
   * lookup falls back to the last-known-good frame.
   *
   * @param entry  - The TThumbnailEntry whose fetch failed.
   * @param reason - The caught error, or a short description when the failure
   *                 is a non-OK HTTP response rather than a thrown error.
   */
  private onFetchError(entry: TThumbnailEntry, reason?: unknown): void {
    entry.status = ThumbnailStatus.FAILED;
    this.mFetchesInFlight.delete(entry.time);
    Log.w(
      BrowserThumbnailStrategy.TAG,
      "fetchThumbnail(): failed for time=" + String(entry.time),
      reason
    );
    // Failed fetches do not block the queue.
    this.prefetchNextImage();
  }

  // ── Private — Live scheduling ──────────────────────────────────────────────

  /**
   * Schedule fetching of live thumbnails as they become available
   * (i.e. once they are LIVE_THRESHOLD_SECONDS behind the live edge).
   *
   * Port of mobi `fetchNextLiveThumbnail()`.
   */
  private scheduleLiveThumbnailFetch(): void {
    const now = Math.floor(Date.now() / 1000);

    if (
      this.mNextLiveThumbnailToFetch === null ||
      this.mNextLiveThumbnailToFetch >= now
    ) {
      return;
    }

    const entry = this.mThumbnailsList.get(this.mNextLiveThumbnailToFetch);
    if (!entry) {
      return;
    }

    // Capture the non-null value in a local variable so the setTimeout
    // callback can safely use it without a type assertion (TypeScript cannot
    // narrow across closure boundaries).
    const nextLiveTime = this.mNextLiveThumbnailToFetch;
    const delayMs = (nextLiveTime - now + LIVE_THRESHOLD_SECONDS) * 1000;

    // Reached from start() AND from every resume via prefetchThumbnails(), so
    // a chain may already be pending. Overwriting its handle without clearing
    // it orphaned that chain: reset() could only cancel the newest one, and the
    // orphan went on to fetch the previous content's frame after a channel
    // change. Keeping exactly one chain alive makes reset() authoritative.
    this.mLiveFetchTimeout = clearTimer(this.mLiveFetchTimeout);
    this.mLiveFetchTimeout = setTimeout(
      () => {
        this.fetchThumbnail(entry);
        this.mNextLiveThumbnailToFetch = nextLiveTime + THUMBNAIL_INTERVAL;
        this.scheduleLiveThumbnailFetch();
      },
      Math.max(delayMs, 0)
    );
  }

  // ── Private — Helpers ──────────────────────────────────────────────────────

  /**
   * The URL to fetch for an entry: its existing blob when one has already been
   * created, otherwise the raw CDN URL (null when that is empty).
   *
   * Returning the blob is what makes re-fetching an already-LOADED entry a
   * no-op rather than a second download — see fetchThumbnail()'s status guard.
   */
  private getCurrentUrlForEntry(entry: TThumbnailEntry): string | null {
    // If we already have the blob, it is the authoritative source.
    if (entry.imgBlob) {
      return entry.imgBlob;
    }

    if (entry.url === "") {
      return null;
    }
    return entry.url;
  }

  /**
   * Return the highest thumbnail time.
   * O(1) — mThumbnails is built in chronological order so the last element
   * always holds the maximum time.
   */
  private getMaxThumbnailTime(): number {
    if (this.mThumbnails.length === 0) {
      return 0;
    }
    return this.mThumbnails[this.mThumbnails.length - 1].time;
  }

  /** Snap to the entry grid anchored at mStartTime. Unclamped upper bound. */
  private snapToThumbnailGrid(absolutePositionSeconds: number): number {
    return snapToStep(
      absolutePositionSeconds,
      this.mStartTime,
      this.mThumbnailsInterval
    );
  }
}
